// hotfix.auth.js
import { getAuth } from "firebase/auth";

// Cache + single-flight wrapper for idToken/idTokenResult
export function installAuthHotfix() {
  const auth = getAuth();
  const cache = new WeakMap(); // user -> {token, result, at, inFlight}

  // TTL to avoid hammering SecureToken. 4–5 min is safe; SDK refreshes before expiry anyway.
  const TTL_MS = 4 * 60 * 1000;

  const now = () => Date.now();
  const isFresh = (entry) => entry && (now() - entry.at) < TTL_MS;

  const wrap = (fnName) => {
    const orig = auth.currentUser?.constructor?.prototype?.[fnName];
    if (!orig || orig.__wrapped) return; // already wrapped or not available

    const wrapped = function(forceRefresh = false) {
      // NEVER force refresh from app code; SDK handles rotation
      const user = this;
      let entry = cache.get(user);
      if (entry?.inFlight) return entry.inFlight;

      // Serve fresh cache
      if (!forceRefresh && isFresh(entry) && entry[fnName]) {
        return Promise.resolve(entry[fnName]);
      }

      // Single flight: make exactly one call and cache it
      const p = (async () => {
        try {
          // DO NOT pass true; let Firebase decide
          const value = await orig.call(user, false);
          entry = cache.get(user) || {};
          entry[fnName] = value;
          entry.at = now();
          cache.set(user, entry);
          return value;
        } catch (error) {
          console.error(`❌ AUTH HOTFIX: Error fetching ${fnName}:`, error);
          throw error;
        } finally {
          entry = cache.get(user) || {};
          entry.inFlight = null;
          cache.set(user, entry);
        }
      })();

      entry = cache.get(user) || {};
      entry.inFlight = p;
      cache.set(user, entry);
      return p;
    };
    wrapped.__wrapped = true;
    auth.currentUser?.constructor?.prototype && (auth.currentUser.constructor.prototype[fnName] = wrapped);
  };

  // Patch both getters (some code uses one or the other)
  wrap("getIdToken");
  wrap("getIdTokenResult");
  
  // Auth hotfix installed silently
}
